import { type VariantProps, cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '@/design-system/lib/cn';

const alertVariants = cva(
  'flex gap-2.5 rounded-block border px-3.5 py-3 text-small [&>svg]:mt-0.5 [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:stroke-[1.8]',
  {
    variants: {
      tone: {
        danger: 'border-status-conflict-line bg-danger-soft text-danger-ink [&>svg]:text-status-conflict',
        warning: 'border-status-partial-line bg-warning-soft text-warning-deep [&>svg]:text-status-partial',
        info: 'border-primary-line-soft bg-primary-faint text-ink-strong [&>svg]:text-primary',
        neutral: 'border-line-input bg-surface-subtle text-ink-secondary [&>svg]:text-ink-muted',
      },
    },
    defaultVariants: { tone: 'neutral' },
  }
);

export type AlertProps = ComponentProps<'div'> & VariantProps<typeof alertVariants>;

/**
 * Mensagem de feedback. O ícone entra como primeiro filho e leva `aria-hidden`. O papel
 * ARIA sai do tom: perigo é assertivo (`alert`), o resto não se anuncia sozinho — passe
 * `role` quando a mensagem nascer de uma ação do usuário.
 */
export function Alert({ className, tone, role, ...props }: AlertProps) {
  return (
    <div
      data-slot="alert"
      role={role ?? (tone === 'danger' ? 'alert' : undefined)}
      className={cn(alertVariants({ tone }), className)}
      {...props}
    />
  );
}

export { alertVariants };
